import { BaseService } from './base';
import {
  SubscriptionPlan,
  UserSubscription,
  UsageDetails,
  Invoice,
  CheckoutSessionResponse,
  BillingPortalResponse,
  ApiResponse,
  PaginatedResponse
} from '../types/subscription';

// Re-export all types for backward compatibility during migration
export type CurrentSubscription = UserSubscription;
export type {
  SubscriptionPlan,
  UserSubscription,
  UsageDetails,
  Invoice,
  CheckoutSessionResponse,
  BillingPortalResponse,
  ApiResponse,
  PaginatedResponse
} from '../types/subscription';

// Shared feature display names (extracted to avoid duplication)
const FEATURE_DISPLAY_NAMES: Record<string, string> = {
  // Basic transformations
  resize: 'Image Resizing',
  format_conversion: 'Format Conversion (JPG, PNG, WebP)',
  color_adjustment: 'Color Adjustments',
  sharpen: 'Image Sharpening',
  blur: 'Blur Effects',
  quality_enhancement: 'Basic Quality Enhancement',
  auto_enhancement: 'Auto Enhancement',

  // AI features
  background_removal: 'AI Background Removal',
  ai_upscale: 'AI Upscaling (2x, 4x)',
  generative_remove: 'Remove Objects with AI',
  generative_fill: 'AI Content-Aware Fill',

  // Advanced AI features
  generative_replace: 'Replace Objects with AI',
  generative_background_replace: 'Replace Backgrounds with AI',
  auto_tagging: 'Auto Product Tagging',
  object_detection: 'AI Object Detection',

  // Support features
  email_support: 'Email Support',
  priority_support: 'Priority Support',

  // Other features
  unlimited_connectors: 'Unlimited Connectors',
  api_access: 'API Access',
  custom_transformations: 'Custom Transformations',
  bulk_operations: 'Bulk Operations',
  priority_processing: 'Priority Processing',
  custom_ai_solutions: 'Custom AI Solutions & Models',
  ai_object_removal: 'AI Object Removal & Replacement'
};

// Feature category definitions
const BASIC_TRANSFORMATION_FEATURES = ['resize', 'format_conversion', 'color_adjustment', 'sharpen', 'blur', 'quality_enhancement', 'auto_enhancement'];
const AI_FEATURES = ['background_removal', 'ai_upscale', 'generative_remove', 'generative_fill', 'ai_object_removal'];
const ADVANCED_AI_FEATURES = ['generative_replace', 'generative_background_replace', 'auto_tagging', 'object_detection', 'custom_ai_solutions'];

// Categorized features type
interface CategorizedFeatures {
  basic_transformations: string[];
  ai_features: string[];
  advanced_ai_features: string[];
  support: string[];
  other: string[];
}

/**
 * Categorize features into groups
 * @param features - The features record from the plan
 * @returns Categorized features object
 */
function categorizeFeatures(features: Record<string, boolean | string>): CategorizedFeatures {
  const categorized: CategorizedFeatures = {
    basic_transformations: [],
    ai_features: [],
    advanced_ai_features: [],
    support: [],
    other: []
  };

  Object.entries(features).forEach(([key, value]) => {
    if (value === true || (typeof value === 'string' && value !== 'false')) {
      const displayName = FEATURE_DISPLAY_NAMES[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      if (BASIC_TRANSFORMATION_FEATURES.includes(key)) {
        categorized.basic_transformations.push(displayName);
      } else if (AI_FEATURES.includes(key)) {
        categorized.ai_features.push(displayName);
      } else if (ADVANCED_AI_FEATURES.includes(key)) {
        categorized.advanced_ai_features.push(displayName);
      } else if (key.includes('support')) {
        categorized.support.push(displayName);
      } else {
        categorized.other.push(displayName);
      }
    }
  });

  return categorized;
}

class SubscriptionService extends BaseService {
  /**
   * Get all available subscription plans
   */
  async getPlans(): Promise<SubscriptionPlan[]> {
    const response = await this.makeRequest<{ plans: SubscriptionPlan[] }>('/subscriptions/plans');
    return response.plans;
  }

  /**
   * Get user's current subscription
   */
  async getCurrentSubscription(): Promise<UserSubscription | null> {
    const response = await this.makeRequest<{ subscription: UserSubscription | null }>('/subscriptions/current');
    return response.subscription;
  }

  /**
   * Validate if user can change to a specific plan
   */
  async validatePlanChange(targetPlanId: string): Promise<{
    validation: {
      allowed: boolean;
      is_downgrade: boolean;
      is_upgrade: boolean;
      violations: Array<{
        type: string;
        message: string;
        current: number;
        limit: number;
        overage: number;
      }>;
      current_usage: {
        credits: number;
        syncs: number;
        stores: number;
      };
      target_limits: {
        credits: number;
        syncs: number;
        stores: number;
      };
      current_plan: {
        id: string;
        name: string;
        limits: {
          credits: number;
          syncs: number;
          stores: number;
        };
      };
    };
  }> {
    const response = await this.makeRequest<any>('/subscriptions/validate-plan-change', {
      method: 'POST',
      body: JSON.stringify({ target_plan_id: targetPlanId }),
    });
    return response;
  }

  /**
   * Create checkout session for a plan
   */
  async createCheckoutSession(planId: string): Promise<CheckoutSessionResponse> {
    const response = await this.makeRequest<CheckoutSessionResponse>('/subscriptions/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId }),
    });
    return response;
  }

  /**
   * Create billing portal session
   */
  async createBillingPortalSession(): Promise<BillingPortalResponse> {
    const response = await this.makeRequest<BillingPortalResponse>('/subscriptions/portal', {
      method: 'POST',
    });
    return response;
  }

  /**
   * Get detailed usage information
   */
  async getUsageDetails(): Promise<UsageDetails> {
    const response = await this.makeRequest<{ usage: UsageDetails }>('/subscriptions/usage');
    return response.usage;
  }

  /**
   * Get invoice history
   */
  async getInvoices(limit: number = 10, offset: number = 0): Promise<{
    invoices: Invoice[];
    pagination: { limit: number; offset: number; total: number };
  }> {
    // Convert offset to page number for backend API
    const page = Math.floor(offset / limit) + 1;
    
    // Use makeFullRequest to get pagination data
    const response = await this.makeFullRequest<Invoice[]>(`/subscriptions/invoices?limit=${limit}&page=${page}`);

    // Convert the standardized pagination format to the expected format
    const pagination = response.pagination ? {
      limit: response.pagination.limit,
      offset: (response.pagination.page - 1) * response.pagination.limit,
      total: response.pagination.total
    } : { limit, offset: 0, total: 0 };
    
    return {
      invoices: response.data || [],
      pagination
    };
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  }

  /**
   * Format plan features based on categories
   */
  formatPlanFeatures(features: Record<string, boolean | string>, planName: string): string[] {
    const categorized = categorizeFeatures(features);
    const formattedFeatures: string[] = [];

    // Category order based on plan
    const categoryOrder: (keyof CategorizedFeatures)[] = planName === 'Enterprise'
      ? ['advanced_ai_features', 'ai_features', 'basic_transformations', 'support', 'other']
      : planName === 'Gold'
      ? ['ai_features', 'basic_transformations', 'support', 'other']
      : ['basic_transformations', 'support', 'other'];

    // Build final feature list in category order
    categoryOrder.forEach(category => {
      if (categorized[category] && categorized[category].length > 0) {
        formattedFeatures.push(...categorized[category]);
      }
    });

    return formattedFeatures;
  }

  /**
   * Get categorized plan features for detailed display
   */
  getCategorizedPlanFeatures(features: Record<string, boolean | string>, _planName: string): Record<string, { title: string; features: string[] }> {
    const categorized = categorizeFeatures(features);
    const result: Record<string, { title: string; features: string[] }> = {};

    if (categorized.basic_transformations.length > 0) {
      result.basic = {
        title: 'Basic Transformations',
        features: categorized.basic_transformations
      };
    }

    if (categorized.ai_features.length > 0) {
      result.ai = {
        title: 'AI-Powered Features',
        features: categorized.ai_features
      };
    }

    if (categorized.advanced_ai_features.length > 0) {
      result.advanced = {
        title: 'Advanced AI Features',
        features: categorized.advanced_ai_features
      };
    }

    if (categorized.support.length > 0 || categorized.other.length > 0) {
      result.additional = {
        title: 'Additional Features',
        features: [...categorized.support, ...categorized.other]
      };
    }

    return result;
  }

  /**
   * Get plan badge color based on plan name
   */
  getPlanBadgeColor(planName: string): string {
    switch (planName.toLowerCase()) {
      case 'starter':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'creator':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'builder':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'growth mini':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'bronze':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'silver':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'gold':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'enterprise':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'custom':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  }

  /**
   * Get usage status color
   */
  getUsageStatusColor(percentage: number): string {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-orange-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  }

  /**
   * Format relative date
   */
  formatRelativeDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  }

  /**
   * Calculate days until billing period end
   */
  getDaysUntilRenewal(periodEnd: string): number {
    const endDate = new Date(periodEnd);
    const now = new Date();
    const diffInMs = endDate.getTime() - now.getTime();
    return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  }

  /**
   * Get plan tier level for comparison
   */
  private getPlanTierLevel(planName: string): number {
    switch (planName.toLowerCase()) {
      case 'trial':
        return 0;
      case 'starter':
        return 1;
      case 'creator':
        return 2;
      case 'builder':
        return 3;
      case 'growth mini':
        return 4;
      case 'bronze':
        return 5;
      case 'silver':
        return 6;
      case 'gold':
        return 7;
      case 'enterprise':
        return 8;
      case 'custom':
        return 9;
      default:
        // Unknown plans get tier 99 to ensure they show as upgrades, not "same"
        return 99;
    }
  }

  /**
   * Compare plan tiers to determine upgrade/downgrade
   */
  comparePlans(currentPlan: string, targetPlan: string): 'upgrade' | 'downgrade' | 'same' {
    const currentTier = this.getPlanTierLevel(currentPlan);
    const targetTier = this.getPlanTierLevel(targetPlan);
    
    if (targetTier > currentTier) return 'upgrade';
    if (targetTier < currentTier) return 'downgrade';
    return 'same';
  }

  /**
   * Get button text based on plan comparison
   */
  getButtonText(currentPlan: string | null, targetPlan: string, isCurrent: boolean): string {
    if (isCurrent) return 'Current Plan';
    if (!currentPlan) return 'Get Started';
    
    const comparison = this.comparePlans(currentPlan, targetPlan);
    switch (comparison) {
      case 'upgrade':
        return 'Upgrade Now';
      case 'downgrade':
        return 'Downgrade';
      case 'same':
        return 'Current Plan';
      default:
        return 'Get Started';
    }
  }

  /**
   * Get upgrade recommendation based on usage
   */
  getUpgradeRecommendation(subscription: UserSubscription): {
    shouldUpgrade: boolean;
    reason: string;
    recommendedPlan: string;
  } | null {
    // Get the next tier plan for recommendations
    const getNextTierPlan = (currentPlan: string): string => {
      const tierOrder = ['Trial', 'Starter', 'Creator', 'Builder', 'Growth Mini', 'Bronze', 'Silver', 'Gold', 'Enterprise', 'Custom'];
      const currentIndex = tierOrder.findIndex(p => p.toLowerCase() === currentPlan.toLowerCase());
      if (currentIndex >= 0 && currentIndex < tierOrder.length - 1) {
        return tierOrder[currentIndex + 1];
      }
      return 'Custom';
    };

    // High usage patterns that suggest upgrade
    if (subscription.usage_percentage > 80) {
      return {
        shouldUpgrade: true,
        reason: `You're using ${subscription.usage_percentage.toFixed(0)}% of your credits`,
        recommendedPlan: getNextTierPlan(subscription.plan_name)
      };
    }

    if (subscription.sync_remaining <= 0) {
      return {
        shouldUpgrade: true,
        reason: 'You\'ve used all your monthly syncs',
        recommendedPlan: getNextTierPlan(subscription.plan_name)
      };
    }

    if (subscription.current_stores >= subscription.store_limit) {
      return {
        shouldUpgrade: true,
        reason: 'You\'ve reached your connector limit',
        recommendedPlan: getNextTierPlan(subscription.plan_name)
      };
    }

    return null;
  }

  /**
   * Get highlighted features for each plan
   */
  getPlanHighlights(planName: string): string[] {
    switch (planName) {
      case 'Starter':
        return [
          'Perfect for individuals',
          '500 credits/month',
          '1 connector',
          'All AI features included'
        ];
      case 'Creator':
        return [
          'Everything in Starter',
          '1,500 credits/month',
          'Great for small catalogs',
          'Unlimited syncs'
        ];
      case 'Builder':
        return [
          'Everything in Creator',
          '4,000 credits/month',
          '3 connectors',
          'Perfect for growing businesses'
        ];
      case 'Growth Mini':
        return [
          'Everything in Builder',
          '10,000 credits/month',
          '10 connectors',
          'Scale your operations'
        ];
      case 'Bronze':
        return [
          'Everything in Growth Mini',
          '30,000 credits/month',
          'Unlimited connectors & syncs',
          'For medium businesses'
        ];
      case 'Silver':
        return [
          'Everything in Bronze',
          '80,000 credits/month',
          'Unlimited connectors & syncs',
          'High-volume processing'
        ];
      case 'Gold':
        return [
          'Everything in Silver',
          'Most popular choice',
          '150,000 credits/month',
          'Priority processing'
        ];
      case 'Enterprise':
        return [
          'Everything in Gold',
          '500,000 credits/month',
          'Dedicated support',
          'Enterprise-grade SLA'
        ];
      case 'Custom':
        return [
          'Everything in Enterprise',
          'Custom credit allocation',
          'Dedicated account manager',
          'Custom integrations & SLA'
        ];
      default:
        return [];
    }
  }
}

export const subscriptionService = new SubscriptionService();